/*
  Ce que l'ecran « Gestion des stages » calcule sur la liste, sans rien
  demander a la base : le libelle d'age, la recherche, les filtres et leurs
  compteurs, et la regle qui dit si un stage peut etre supprime.

  Pur, donc teste a part (__tests__/catalogue.test.ts).
*/
import type { TypeDActivite } from '../seo/stage';

/** Les champs de la fiche dont ces calculs ont besoin. */
export interface StageDuCatalogue {
  id: string;
  title: string;
  description: string;
  age_min: number;
  age_max: number;
  image_url: string | null;
  active: boolean | null;
  type_activite?: TypeDActivite;
  /** Matériel propre au stage, une ligne par élément ; repris dans le courriel d'avant-stage. */
  materiel_a_prevoir?: string[] | null;
  /** Éclosion peut prêter ce matériel sur demande. */
  pret_materiel_possible?: boolean | null;
  /** Toutes les sessions, archivees comprises. */
  nb_sessions: number;
  /** Sessions non archivees qui ne sont pas encore terminees. */
  nb_sessions_a_venir: number;
}

export type FiltreStatut = 'tous' | 'actifs' | 'inactifs' | 'sans_image';
export type FiltreType = 'tous' | TypeDActivite;

export interface Filtres {
  recherche: string;
  type: FiltreType;
  statut: FiltreStatut;
}

export const FILTRES_PAR_DEFAUT: Filtres = { recherche: '', type: 'tous', statut: 'tous' };

/*
  `active` est nullable en base, avec `true` pour defaut : une ligne sans
  valeur est active, comme le lit deja la programmation (`active !== false`).
*/
export const estActif = (stage: Pick<StageDuCatalogue, 'active'>) => stage.active !== false;

const nombre = (n: number) => n.toLocaleString('fr-BE', { maximumFractionDigits: 1 });

/**
 * « 6 – moins de 13 ans ». L'age maximum est EXCLU (l'enfant doit avoir
 * strictement moins) : « 6 - 13 ans » laissait croire l'inverse.
 */
export function libelleAge(ageMin: number, ageMax: number): string {
  return `${nombre(ageMin)} – moins de ${nombre(ageMax)} ans`;
}

// Sans accents ni casse : « theatre » trouve « Théâtre ».
const normaliser = (texte: string) =>
  texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const typeDuStage = (stage: StageDuCatalogue): TypeDActivite => stage.type_activite ?? 'stage';

function passeLeStatut(stage: StageDuCatalogue, statut: FiltreStatut): boolean {
  switch (statut) {
    case 'actifs':
      return estActif(stage);
    case 'inactifs':
      return !estActif(stage);
    case 'sans_image':
      return !stage.image_url;
    default:
      return true;
  }
}

export function filtrerStages<S extends StageDuCatalogue>(stages: S[], filtres: Filtres): S[] {
  const recherche = normaliser(filtres.recherche);
  return stages.filter(
    (stage) =>
      (!recherche || normaliser(stage.title).includes(recherche)) &&
      (filtres.type === 'tous' || typeDuStage(stage) === filtres.type) &&
      passeLeStatut(stage, filtres.statut)
  );
}

/*
  Les compteurs d'un groupe de filtres tiennent compte des AUTRES filtres
  (et de la recherche), pas du leur : « Plaines (7) » dit ce qu'on obtiendra
  en cliquant, compte tenu du statut deja choisi.
*/
export function compterParType(stages: StageDuCatalogue[], filtres: Filtres): Record<FiltreType, number> {
  const base = filtrerStages(stages, { ...filtres, type: 'tous' });
  const compte: Record<FiltreType, number> = { tous: base.length, plaine: 0, stage: 0, sejour: 0 };
  for (const stage of base) compte[typeDuStage(stage)] += 1;
  return compte;
}

export function compterParStatut(stages: StageDuCatalogue[], filtres: Filtres): Record<FiltreStatut, number> {
  const base = filtrerStages(stages, { ...filtres, statut: 'tous' });
  return {
    tous: base.length,
    actifs: base.filter((s) => passeLeStatut(s, 'actifs')).length,
    inactifs: base.filter((s) => passeLeStatut(s, 'inactifs')).length,
    sans_image: base.filter((s) => passeLeStatut(s, 'sans_image')).length,
  };
}

/*
  Une session, meme archivee, garde son stage : la base refuse la
  suppression (cle etrangere), et les inscriptions et factures passees y
  renvoient. Un stage qui a servi se desactive, il ne se supprime pas.
*/
export const peutEtreSupprime = (stage: Pick<StageDuCatalogue, 'nb_sessions'>) => stage.nb_sessions === 0;

export function libelleSessions(stage: Pick<StageDuCatalogue, 'nb_sessions' | 'nb_sessions_a_venir'>): string {
  if (stage.nb_sessions_a_venir > 0) {
    return `${stage.nb_sessions_a_venir} à venir`;
  }
  if (stage.nb_sessions > 0) {
    return 'Aucune à venir';
  }
  return 'Aucune';
}

export const trierParTitre = <S extends Pick<StageDuCatalogue, 'title'>>(stages: S[]): S[] =>
  [...stages].sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }));
